import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Banknote,
  Calendar,
  CreditCard,
  Eye,
  FileText,
  Filter,
  Info,
  Package,
  RotateCcw,
  Search,
  Trash2,
  Truck,
} from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import { Spinner } from "../../components/ui/Spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { useBlacklist } from "../../context/BlacklistContext";
import { fetchWithAuth } from "../../hooks/useAuth";
import { Order, OrderStatus, PaymentStatus } from "../../prisma-types";
// import { Order, OrderFilters, OrdersResponse } from "../../types/orders";
import { formatDate, formatPrice } from "../../utils/format";

const STATUS_TRANSLATIONS: Record<OrderStatus, string> = {
  PENDING: "În așteptare",
  PAID: "Plătită",
  PROCESSING: "În procesare",
  SHIPPED: "Expediată",
  DELIVERED: "Livrată",
  CANCELLED: "Anulată",
};

const PAYMENT_STATUS_TRANSLATIONS: Record<PaymentStatus, string> = {
  PENDING: "În așteptare",
  PROCESSING: "În procesare",
  COMPLETED: "Finalizată",
  FAILED: "Eșuată",
  REFUNDED: "Rambursată",
};

import { samedayStatusConfig } from "../../config/sameday.status.config";
import OrdersHeader from "./OrdersHeader";
import { OrderFilters } from "@/types/orders";

const getStatusColor = (statusId: number): string => {
  const status = samedayStatusConfig.statusMapping[statusId];
  switch (status) {
    case "DELIVERED":
      return "bg-green-100 text-green-800";
    case "IN_TRANSIT":
      return "bg-yellow-100 text-yellow-800";
    case "FAILED":
      return "bg-red-100 text-red-800";
    case "RETURNED":
      return "bg-purple-100 text-purple-800";
    case "CANCELED":
      return "bg-red-100 text-red-800";
    case "REGISTERED":
    default:
      return "bg-blue-100 text-blue-800";
  }
};

const ORDER_STATUSES = Object.values(OrderStatus);
const PAYMENT_STATUSES = Object.values(PaymentStatus);

const paymentStatusColors: Record<PaymentStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
  REFUNDED: "bg-gray-100 text-gray-800",
};

export function OrdersPage() {
  const queryClient = useQueryClient();
  const { getPhonesSet } = useBlacklist();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = React.useState<OrderFilters>({
    search: "",
    status: undefined,
    paymentStatus: undefined,
    shippingStatus: undefined,
    dateFrom: "",
    dateTo: "",
  });

  // Fetch orders with filters
  const [page, setPage] = React.useState(() => {
    const pageParam = searchParams.get("page");
    const initial = pageParam ? parseInt(pageParam, 10) : 1;
    return Number.isNaN(initial) || initial < 1 ? 1 : initial;
  });
  const [pageSize] = React.useState(10);

  // Keep URL in sync with current page without fighting browser back stack
  React.useEffect(() => {
    const currentParam = searchParams.get("page") || "1";
    if (String(page) !== currentParam) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("page", String(page));
      // Use replace only when param is missing or initial; otherwise push to allow normal back
      const useReplace =
        currentParam === "1" && page !== 1 && !searchParams.has("page");
      setSearchParams(nextParams, { replace: useReplace });
    }
  }, [page]);

  const { data: ordersResponse, isLoading } = useQuery<Order[]>({
    refetchInterval: 15000, // Refetch every 15 seconds
    queryKey: ["orders", filters, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.paymentStatus)
        params.append("paymentStatus", filters.paymentStatus);
      if (filters.shippingStatus)
        params.append("shippingStatus", filters.shippingStatus);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      params.append("page", page.toString());
      params.append("limit", pageSize.toString());

      const response = await fetchWithAuth(`/api/admin/orders?${params}`);
      if (!response.ok) throw new Error("Eroare la încărcarea comenzilor");
      return response.json();
    },
  });

  console.log("ordersResponse", ordersResponse);

  // Prepare blacklisted phone set once (cached via context TTL). Do not refetch on page changes
  const [blacklistedPhones, setBlacklistedPhones] =
    React.useState<Set<string> | null>(null);
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const set = await getPhonesSet();
        if (mounted) setBlacklistedPhones(set);
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [getPhonesSet]);

  const normalizePhone = (phone?: string): string => {
    let digits = (phone || "").replace(/\D/g, "");
    if (digits.startsWith("0040")) digits = digits.slice(4);
    else if (digits.startsWith("40")) digits = digits.slice(2);
    else if (digits.startsWith("+40")) digits = digits.slice(3);
    if (!digits.startsWith("0") && digits.length === 9) digits = "0" + digits;
    if (digits.length > 10) digits = digits.slice(-10);
    return digits;
  };

  // Track per-order loading state for row actions (restore/cancel)
  const [loadingByOrderId, setLoadingByOrderId] = React.useState<
    Record<number, "restore" | "cancel" | undefined>
  >({});

  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      {/* orders header */}
      <OrdersHeader />

      {/* Filters */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold text-foreground">Filtre</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Caută după ID, client, email..."
              value={filters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
            />
          </div>

          <Select
            value={filters.status ?? "ALL"}
            onValueChange={(value) =>
              setFilters((f) => ({
                ...f,
                status: value === "ALL" ? undefined : (value as OrderStatus),
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate statusurile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate statusurile</SelectItem>
              {ORDER_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_TRANSLATIONS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.paymentStatus ?? "ALL"}
            onValueChange={(value) =>
              setFilters((f) => ({
                ...f,
                paymentStatus:
                  value === "ALL" ? undefined : (value as PaymentStatus),
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate plățile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate plățile</SelectItem>
              {PAYMENT_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {PAYMENT_STATUS_TRANSLATIONS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.shippingStatus ?? "ALL"}
            onValueChange={(value) =>
              setFilters((f) => ({
                ...f,
                shippingStatus: value === "ALL" ? undefined : value,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate livrările" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate livrările</SelectItem>
              {Object.entries(samedayStatusConfig.statusLabels).map(
                ([id, label]) => (
                  <SelectItem key={id} value={id}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters((f) => ({ ...f, dateFrom: e.target.value }))
              }
            />
          </div>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters((f) => ({ ...f, dateTo: e.target.value }))
              }
            />
          </div>
        </div>
      </Card>

      {/* Legend */}
      <CardInstructiuni />

      {/* Orders Table */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Plată</TableHead>
              <TableHead>Livrare</TableHead>
              <TableHead>AWB</TableHead>
              <TableHead>Factură</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : ordersResponse?.orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Nu există comenzi
                </TableCell>
              </TableRow>
            ) : (
              ordersResponse?.orders.map((order: Order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {blacklistedPhones &&
                      order.metadata?.customerPhone &&
                      blacklistedPhones.has(
                        normalizePhone(order.metadata.customerPhone)
                      ) ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 font-bold text-xs">
                          B
                        </span>
                      ) : null}
                      {order.lockerId && (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs">
                          <Package className="w-3 h-3" />
                        </span>
                      )}
                      {order.id}
                      <Badge
                        variant="outline"
                        className={`px-2 py-0.5 flex items-center gap-1 ${
                          order.payment?.method === "NETOPIA"
                            ? "bg-blue-50 text-blue-700 ring-1 ring-blue-700/10"
                            : "bg-green-50 text-green-700 ring-1 ring-green-700/10"
                        }`}
                      >
                        {order.payment?.method === "NETOPIA" ? (
                          <>
                            <CreditCard className="w-3 h-3" />
                            Card
                          </>
                        ) : (
                          <>
                            <Banknote className="w-3 h-3" />
                            Ramburs
                          </>
                        )}
                      </Badge>
                      {order.metadata?.observatii && (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-purple-100 text-purple-700 font-bold text-xs">
                          <Info className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>
                    <Link
                      to={`/customers/${order.userId}`}
                      className="flex flex-col"
                    >
                      <div className="flex items-center gap-2">
                        <span>
                          {order.metadata?.customerName || order.user.name}
                        </span>
                        {order.userDeliveredOrdersCount > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-green-100 text-green-700 font-bold text-xs">
                            {order.userDeliveredOrdersCount}
                          </span>
                        )}
                        {order.userPendingOrdersCount > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-yellow-100 text-yellow-700 font-bold text-xs">
                            {order.userPendingOrdersCount}
                          </span>
                        )}

                        {order.metadata?.company?.cui && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-blue-100 text-blue-700 font-bold text-xs">
                            F
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {order.metadata?.customerEmail || order.user.email}
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell>{formatPrice(order.totalPrice)}</TableCell>
                  <TableCell>
                    <Badge
                      className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                        order.status === "CANCELLED"
                          ? "bg-red-100 text-red-800 ring-red-200"
                          : paymentStatusColors[
                              (order.payment?.status ||
                                "PENDING") as PaymentStatus
                            ] + " ring-current/20"
                      }`}
                    >
                      {order.status === "CANCELLED"
                        ? "Anulată"
                        : PAYMENT_STATUS_TRANSLATIONS[
                            (order.payment?.status ||
                              "PENDING") as PaymentStatus
                          ]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                        order.status === "CANCELLED"
                          ? "bg-red-100 text-red-800 ring-red-200"
                          : order.awbNumber
                          ? getStatusColor(
                              order.metadata?.shippingStatusDetails?.statusId ||
                                1
                            ) + " ring-current/20"
                          : "bg-yellow-100 text-yellow-800 ring-yellow-200"
                      }`}
                    >
                      {order.status === "CANCELLED"
                        ? "Anulată"
                        : order.awbNumber
                        ? order.metadata?.shippingStatusDetails?.statusId
                          ? samedayStatusConfig.statusLabels[
                              order.metadata.shippingStatusDetails.statusId
                            ]
                          : "Comandă înregistrată"
                        : order.status === "PENDING"
                        ? "În așteptare"
                        : order.status === "PAID"
                        ? "Plătită"
                        : order.status === "PROCESSING"
                        ? "În procesare"
                        : "În așteptare"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {order.awbNumber ? (
                      <Button
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => {
                          const awbPart = order.awbNumber?.split("NBLN")[1];
                          if (awbPart) {
                            window.open(
                              `https://eawb.sameday.ro/awb/information/${awbPart}`,
                              "_blank"
                            );
                          }
                        }}
                      >
                        <Truck className="w-4 h-4" />
                        {order.awbNumber}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {order.invoiceNumber ? (
                      <Button
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => window.open(order.invoiceUrl, "_blank")}
                      >
                        <FileText className="w-4 h-4" />
                        {order.invoiceSeries}/{order.invoiceNumber}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="link" asChild>
                        <Link
                          to={`/orders/${order.id}/`}
                          className="flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Detalii
                        </Link>
                      </Button>
                      {order.status === "CANCELLED" ? (
                        <Button
                          variant="default"
                          size="sm"
                          disabled={loadingByOrderId[order.id] === "restore"}
                          onClick={async () => {
                            if (
                              window.confirm(
                                "Sigur doriți să restaurați această comandă anulată?"
                              )
                            ) {
                              try {
                                setLoadingByOrderId((s) => ({
                                  ...s,
                                  [order.id]: "restore",
                                }));
                                const response = await fetchWithAuth(
                                  `/api/admin/orders/${order.id}/restore`,
                                  { method: "POST" }
                                );
                                if (!response.ok) {
                                  const err = await response
                                    .json()
                                    .catch(() => ({} as any));
                                  throw new Error(
                                    err?.message ||
                                      "Eroare la restaurarea comenzii"
                                  );
                                }

                                // Refetch orders after restore
                                await queryClient.invalidateQueries({
                                  queryKey: ["orders"],
                                });
                                toast.success(
                                  "Comanda a fost restaurată cu succes"
                                );
                              } catch (error) {
                                toast.error(
                                  error instanceof Error
                                    ? error.message
                                    : "Eroare la restaurarea comenzii"
                                );
                                console.error("Error restoring order:", error);
                              } finally {
                                setLoadingByOrderId((s) => ({
                                  ...s,
                                  [order.id]: undefined,
                                }));
                              }
                            }
                          }}
                        >
                          {loadingByOrderId[order.id] === "restore" ? (
                            <>
                              <Spinner className="w-4 h-4 mr-1" /> Restaurare...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-1" /> Restaurează
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={loadingByOrderId[order.id] === "cancel"}
                          onClick={async () => {
                            if (
                              window.confirm(
                                "Sigur doriți să anulați această comandă? Această acțiune va anula și AWB-ul și factura asociată."
                              )
                            ) {
                              try {
                                setLoadingByOrderId((s) => ({
                                  ...s,
                                  [order.id]: "cancel",
                                }));
                                const response = await fetchWithAuth(
                                  `/api/admin/orders/${order.id}/status`,
                                  {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                      status: "CANCELLED",
                                    }),
                                  }
                                );

                                if (!response.ok) {
                                  throw new Error(
                                    "Eroare la anularea comenzii"
                                  );
                                }

                                // Refetch orders after cancellation
                                queryClient.invalidateQueries({
                                  queryKey: ["orders"],
                                });
                                toast.success(
                                  "Comanda a fost anulată cu succes"
                                );
                              } catch (error) {
                                toast.error("Eroare la anularea comenzii");
                                console.error("Error cancelling order:", error);
                              } finally {
                                setLoadingByOrderId((s) => ({
                                  ...s,
                                  [order.id]: undefined,
                                }));
                              }
                            }
                          }}
                        >
                          {loadingByOrderId[order.id] === "cancel" ? (
                            <>
                              <Spinner className="w-4 h-4 mr-1" /> Anulare...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-1" /> Anulează
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {ordersResponse && ordersResponse.total > pageSize && (
        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            <Button
              className="flex items-center gap-2  transition-colors duration-200"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">
                Pagina {page} din {Math.ceil(ordersResponse.total / pageSize)}
              </span>
            </div>
            <Button
              className="flex items-center gap-2  transition-colors duration-200"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil(ordersResponse.total / pageSize)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const CardInstructiuni = () => {
  return (
    <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex gap-10 text-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 font-bold text-xs">
              B
            </span>
          </div>
          <span className="text-muted-foreground">
            Telefon client pe blacklist
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs">
              <Package className="w-3 h-3" />
            </span>
          </div>
          <span className="text-muted-foreground">Comandă locker</span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-purple-100 text-purple-700 font-bold text-xs">
              <Info className="w-4 h-4" />
            </span>
          </div>
          <span className="text-muted-foreground">Observații la comandă</span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-blue-100 text-blue-700 font-bold text-xs">
              F
            </span>
          </div>
          <span className="text-muted-foreground">Client firmă (are CUI)</span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-green-100 text-green-700 font-bold text-xs">
              #
            </span>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-xl bg-yellow-100 text-yellow-700 font-bold text-xs">
              #
            </span>
          </div>
          <span className="text-muted-foreground">
            Nr. comenzi livrate/în curs (client)
          </span>
        </div>
      </div>
    </Card>
  );
};
